import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import type { AutocompleteInputChangeReason, AutocompleteProps } from "@mui/material";
import { Autocomplete } from "@mui/material";
import { Chip } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import classNames from "classnames";
import { tagsColors } from "constants/colors";
import { useDebounce } from "hooks/useDebounce";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import type { Dispatch, Ref, SetStateAction } from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import StyledTextField from "../StyledTextField/StyledTextField";
import styles from "./style.module.scss";

type OptionType = { id: number; name: string; color?: number; disabled?: boolean; displayName?: string };
type ChipsAutocomplete = {
    options: OptionType[];
    label: string;
    noOptionsText: string;
    onSelection?: (tags: string[]) => void;
    filter: string;
    chosenTags: { id: number; name: string; color: number }[] | string[];
    setChosenTags: Dispatch<SetStateAction<string[]>>;
    minWidth?: string;
    isLoading?: boolean;
    /**
     * When provided, the parent owns input filtering (e.g. server-side search).
     * Client-side filtering of `options` is then skipped.
     */
    onInputChange?: (value: string, reason: AutocompleteInputChangeReason) => void;
} & Pick<AutocompleteProps<OptionType, boolean, boolean, boolean>, "size" | "limitTags">;

type TagOption = { id: number; name: string; color?: number; displayName?: string };

const getTagOptionLabel = (option: TagOption) => option.displayName ?? option.name;

const getValueFromChosenTags = (tags: TagOption[] | string[], opts: { id: number; name: string }[]): TagOption[] => {
    const first = tags[0];
    const areObjects = typeof first === "object" && first !== null && "name" in first;
    return areObjects ? (tags as TagOption[]) : opts.filter((opt) => (tags as string[]).includes(opt.name));
};

const ChipsAutocomplete = ({
    options,
    label,
    noOptionsText,
    onSelection,
    filter,
    chosenTags,
    setChosenTags,
    minWidth,
    isLoading,
    onInputChange,
    ...props
}: ChipsAutocomplete) => {
    const [t] = useTranslation();
    const [autocompleteValue, setAutocompleteValue] = useState<any>([]);
    const [inputValue, setInputValue] = useState("");
    const [openPopper, setOpenPopper] = useState(false);
    const { paginatedData: _paginatedOptions, paginatedElRef } = useInfiniteScroll(options, openPopper);
    const debouncedInputValue = useDebounce(inputValue, 300);

    /**
     * @Note Tags options are virtualized paginated,
     * So when searching, we need to filter on the entire options array.
     * When `onInputChange` is provided, the parent already filters server-side,
     * so we hand back `options` unchanged.
     */
    const paginatedOptions = useMemo(() => {
        if (onInputChange) return options;
        if (debouncedInputValue) {
            return options.filter((option) => option?.name?.toLowerCase().includes(debouncedInputValue.toLowerCase()));
        }
        return _paginatedOptions;
    }, [_paginatedOptions, debouncedInputValue, options, onInputChange]);

    // Sync internal value with chosenTags from parent (e.g. after async load of channels)
    useEffect(() => {
        if (chosenTags.length === 0) {
            setAutocompleteValue([]);
        } else {
            setAutocompleteValue(getValueFromChosenTags(chosenTags, options));
        }
    }, [chosenTags, options]);
    /**
     * Handles change of tags (adding or deleting) and sends info up to parent component
     * @param tags The tag object
     */
    const handleTagsChange = (tags: ChipsAutocomplete["options"]) => {
        const nameChosenTags = tags.map((tag) => tag.name);
        setChosenTags(nameChosenTags);
        onSelection && onSelection(nameChosenTags);
        setAutocompleteValue(tags);
    };

    const renderInputAutocomplete = (params: any) => {
        const style = {
            ".MuiOutlinedInput-root": {
                input: {
                    width: `${chosenTags.length > 0 || filter.length > 1 ? "1rem" : "2rem"}`,
                    padding: `${chosenTags.length > 0 && "0px !important"}`,
                },
            },
        };
        return (
            <StyledTextField
                {...params}
                value={inputValue}
                placeholder={chosenTags.length > 0 ? "" : t("Stock.FilterAll")}
                className={styles["autocomplete-input"]}
                sx={style}
            />
        );
    };

    const renderOptionAutocomplete = (props: any, option: ChipsAutocomplete["options"][number], { selected }: any) => {
        const optionLabel = getTagOptionLabel(option);
        return (
            <li
                {...props}
                className={classNames("py-0.5", {
                    [styles["selected"]]: selected,
                    [styles["disabled"]]: option.disabled,
                })}
            >
                <Checkbox
                    icon={<CheckBoxOutlineBlankIcon />}
                    checkedIcon={<CheckBoxIcon color="primary" />}
                    checked={selected}
                    disabled={option.disabled}
                    size="small"
                    sx={{ opacity: 0.8 }}
                />
                <span className="line-clamp-2 wrap-anywhere" title={optionLabel}>
                    {optionLabel}
                </span>
            </li>
        );
    };

    const renderAutocompleteTags = (options: ChipsAutocomplete["options"], getTagProps: any) => {
        return options.map((option, index: number) => {
            const tagColor = tagsColors.find((color: any) => color.id === option.color);
            return (
                <>
                    <Chip
                        {...getTagProps({ index })}
                        className={styles["autocomplete-chip"]}
                        label={getTagOptionLabel(option)}
                        sx={{
                            background: `${tagColor?.backgroundColor}`,
                            color: `${tagColor?.textColor}`,
                        }}
                    />
                    <span className={styles["autocomplete-input-number"]}>
                        {chosenTags.length > 1 && `+${chosenTags.length - 1}`}
                    </span>
                </>
            );
        });
    };

    return (
        <div className={styles["chips-autocomplete"]} style={{ minWidth: `${minWidth ? minWidth : ""}` }}>
            <span className={classNames(styles["autocomplete-label"], "whitespace-nowrap")}>{label}</span>
            <Autocomplete
                size="small"
                value={autocompleteValue}
                multiple
                limitTags={1}
                disableCloseOnSelect
                options={paginatedOptions}
                getOptionLabel={(option) => getTagOptionLabel(option)}
                getOptionDisabled={(option) => !!option.disabled}
                isOptionEqualToValue={(option, value) => option?.id === value?.id || option?.name === value?.name}
                getOptionKey={(option) => option.id ?? option.name}
                renderInput={renderInputAutocomplete}
                renderOption={renderOptionAutocomplete}
                componentsProps={{
                    // Popper is wider than the input so long tag names stay readable
                    popper: { className: "chips-autocomplete-popper min-w-80 max-w-[90vw]", placement: "bottom-start" },
                }}
                onChange={(e, value) => handleTagsChange(value)}
                ListboxProps={{ className: "chips-autocomplete-list", ref: paginatedElRef as Ref<Element> }}
                renderTags={renderAutocompleteTags}
                openOnFocus
                noOptionsText={noOptionsText}
                onOpen={() => setOpenPopper(true)}
                onClose={() => setOpenPopper(false)}
                onInputChange={(_e: any, value, reason) => {
                    setInputValue(value);
                    onInputChange?.(value, reason);
                }}
                loading={isLoading}
                {...props}
            />
        </div>
    );
};

export default ChipsAutocomplete;
